import { useEffect, useMemo, useState } from "react";
import { AuthLayout } from "./auth-layout";
import { ApiError } from "@/api/client";
import { useSession } from "@/api/session";
import {
  useCompleteOnboarding,
  useMyDocuments,
  useOnboardingStatus,
  useSaveMyProfile,
  useSaveOnboardingDates,
  useUploadMyDocument,
  type ProfileInput,
} from "@/api/queries";
import type { DocType, Gender } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Field, Input, LabelledInput, Select, Textarea } from "@/components/ui/field";
import { Icon } from "@/components/ui/icon";
import { SkeletonText } from "@/components/ui/states";
import { toast } from "@/components/ui/toast";
import { DOC_TYPE_LABEL, profileFieldLabel } from "@/lib/labels";
import { cn } from "@/lib/cn";

const REQUIRED_DOCS: DocType[] = ["aadhaar", "pan", "cancelled_cheque"];
const STEPS = ["Your details", "Bank & address", "Documents"] as const;

/**
 * Mandatory onboarding. Nothing else in the app is reachable until the backend
 * says `complete`, and that check is re-run server-side on submit — so this
 * screen mirrors the requirement list rather than deciding it.
 */
export function OnboardingPage() {
  const logout = useSession((s) => s.logout);
  const adoptToken = useSession((s) => s.adoptToken);
  const status = useOnboardingStatus();
  const documents = useMyDocuments();
  const saveDates = useSaveOnboardingDates();
  const saveProfile = useSaveMyProfile();
  const complete = useCompleteOnboarding();

  const [step, setStep] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const [dateOfBirth, setDateOfBirth] = useState("");
  const [dateJoined, setDateJoined] = useState("");
  const [gender, setGender] = useState<Gender | "">("");
  const [profile, setProfile] = useState<ProfileInput>({});
  const [sameAsCurrent, setSameAsCurrent] = useState(false);

  // Re-populate from the server so a reload mid-onboarding never loses progress.
  useEffect(() => {
    if (status.data) {
      setDateOfBirth((current) => current || status.data.dateOfBirth || "");
      setDateJoined((current) => current || status.data.dateJoined || "");
    }
  }, [status.data]);

  useEffect(() => {
    const saved = documents.data?.profile;
    if (!saved) return;
    setGender((current) => current || saved.gender || "");
    setSameAsCurrent(Boolean(saved.same_as_current));
    setProfile((current) => ({
      accountNumber: current.accountNumber ?? saved.account_number ?? "",
      accountHolderName: current.accountHolderName ?? saved.account_holder_name ?? "",
      bankName: current.bankName ?? saved.bank_name ?? "",
      ifscCode: current.ifscCode ?? saved.ifsc_code ?? "",
      currentAddress: current.currentAddress ?? saved.current_address ?? "",
      permanentAddress: current.permanentAddress ?? saved.permanent_address ?? "",
      bloodGroup: current.bloodGroup ?? saved.blood_group ?? "",
      aadhaarNumber: current.aadhaarNumber ?? saved.aadhaar_number ?? "",
      panNumber: current.panNumber ?? saved.pan_number ?? "",
      emergencyContact1Name: current.emergencyContact1Name ?? saved.emergency_contact_1_name ?? "",
      emergencyContact1Number: current.emergencyContact1Number ?? saved.emergency_contact_1_number ?? "",
      emergencyContact2Name: current.emergencyContact2Name ?? saved.emergency_contact_2_name ?? "",
      emergencyContact2Number: current.emergencyContact2Number ?? saved.emergency_contact_2_number ?? "",
    }));
  }, [documents.data?.profile]);

  const uploaded = useMemo(
    () => new Set((documents.data?.documents ?? []).map((d) => d.doc_type)),
    [documents.data?.documents],
  );

  const set = (key: keyof ProfileInput) => (value: string) =>
    setProfile((current) => ({ ...current, [key]: value }));

  async function persist() {
    setError(null);
    const payload: ProfileInput = { ...profile, sameAsCurrent };
    if (gender) payload.gender = gender;
    await saveProfile.mutateAsync(payload);
  }

  async function onNext() {
    setError(null);
    try {
      if (step === 0) {
        if (!dateOfBirth || !dateJoined) {
          setError("Both dates are required.");
          return;
        }
        if (!gender) {
          setError("Select your gender. It is what drives real leave eligibility.");
          return;
        }
        await saveDates.mutateAsync({ dateOfBirth, dateJoined });
        await persist();
      }
      if (step === 1) await persist();
      setStep((current) => Math.min(STEPS.length - 1, current + 1));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save that. Try again.");
    }
  }

  async function onFinish() {
    setError(null);
    try {
      const result = await complete.mutateAsync();
      adoptToken(result.token);
      toast.success("You're all set. Welcome to Mentoria.");
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not finish setup. Check that everything above is filled in.",
      );
      void status.refetch();
    }
  }

  const missing = status.data;
  const busy = saveDates.isPending || saveProfile.isPending || complete.isPending;

  return (
    <AuthLayout
      wide
      title="Finish setting up your account"
      sub="Mentoria needs these details on file before you can use the system. Everything here is stored against your own record only."
    >
      <ol className="mb-6 flex flex-wrap items-center gap-x-2 gap-y-2">
        {STEPS.map((label, index) => (
          <li key={label} className="flex items-center gap-2">
            <span
              className={cn(
                "flex h-6 w-6 items-center justify-center rounded-full font-mono text-[11px]",
                index < step
                  ? "bg-good-wash text-good"
                  : index === step
                    ? "bg-accent text-accent-ink"
                    : "bg-surface-sunken text-faint",
              )}
            >
              {index < step ? <Icon name="check" size={16} /> : index + 1}
            </span>
            <span className={cn("text-[13px]", index === step ? "font-medium text-ink" : "text-muted")}>
              {label}
            </span>
            {index < STEPS.length - 1 && <span className="mx-1 h-px w-6 bg-line" aria-hidden />}
          </li>
        ))}
      </ol>

      {error && (
        <p
          role="alert"
          className="mb-4 flex items-start gap-2 rounded-lg bg-bad-wash px-3 py-2.5 text-[13px] leading-relaxed text-bad"
        >
          <Icon name="warning-circle" size={16} className="mt-0.5" />
          <span>{error}</span>
        </p>
      )}

      {status.isLoading ? (
        <SkeletonText lines={6} />
      ) : (
        <>
          {step === 0 && (
            <div className="grid gap-4 sm:grid-cols-2">
              <LabelledInput
                label="Date of birth"
                type="date"
                required
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
              />
              <LabelledInput
                label="Date you joined Mentoria"
                type="date"
                required
                hint="Used for your work anniversary and leave accrual."
                value={dateJoined}
                onChange={(e) => setDateJoined(e.target.value)}
              />
              <Field label="Gender" htmlFor="ob-gender" required hint="Drives Menstrual Leave eligibility.">
                <Select
                  id="ob-gender"
                  value={gender}
                  onChange={(e) => setGender(e.target.value as Gender)}
                >
                  <option value="">Select</option>
                  <option value="Female">Female</option>
                  <option value="Male">Male</option>
                  <option value="Other">Other</option>
                </Select>
              </Field>
              <Field label="Blood group" htmlFor="ob-blood" hint="Optional.">
                <Input
                  id="ob-blood"
                  placeholder="O+"
                  value={profile.bloodGroup ?? ""}
                  onChange={(e) => set("bloodGroup")(e.target.value)}
                />
              </Field>
              <LabelledInput
                label="Aadhaar number"
                required
                className="sm:col-span-1"
                value={profile.aadhaarNumber ?? ""}
                onChange={(e) => set("aadhaarNumber")(e.target.value)}
              />
              <LabelledInput
                label="PAN number"
                required
                value={profile.panNumber ?? ""}
                onChange={(e) => set("panNumber")(e.target.value)}
              />
            </div>
          )}

          {step === 1 && (
            <div className="flex flex-col gap-5">
              <fieldset className="grid gap-4 sm:grid-cols-2">
                <legend className="mb-2 text-[13px] font-medium text-ink">Bank details</legend>
                <LabelledInput
                  label="Account holder name"
                  required
                  value={profile.accountHolderName ?? ""}
                  onChange={(e) => set("accountHolderName")(e.target.value)}
                />
                <LabelledInput
                  label="Account number"
                  required
                  value={profile.accountNumber ?? ""}
                  onChange={(e) => set("accountNumber")(e.target.value)}
                />
                <LabelledInput
                  label="Bank name"
                  required
                  value={profile.bankName ?? ""}
                  onChange={(e) => set("bankName")(e.target.value)}
                />
                <LabelledInput
                  label="IFSC code"
                  required
                  value={profile.ifscCode ?? ""}
                  onChange={(e) => set("ifscCode")(e.target.value)}
                />
              </fieldset>

              <fieldset className="flex flex-col gap-4">
                <legend className="mb-2 text-[13px] font-medium text-ink">Address</legend>
                <Field label="Current address" htmlFor="ob-current" required>
                  <Textarea
                    id="ob-current"
                    value={profile.currentAddress ?? ""}
                    onChange={(e) => set("currentAddress")(e.target.value)}
                  />
                </Field>
                <label className="flex items-center gap-2 text-[13px] text-ink-soft">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-line-strong accent-accent"
                    checked={sameAsCurrent}
                    onChange={(e) => setSameAsCurrent(e.target.checked)}
                  />
                  Permanent address is the same as my current address
                </label>
                {!sameAsCurrent && (
                  <Field label="Permanent address" htmlFor="ob-permanent" required>
                    <Textarea
                      id="ob-permanent"
                      value={profile.permanentAddress ?? ""}
                      onChange={(e) => set("permanentAddress")(e.target.value)}
                    />
                  </Field>
                )}
              </fieldset>

              <fieldset className="grid gap-4 sm:grid-cols-2">
                <legend className="mb-2 text-[13px] font-medium text-ink">Emergency contacts</legend>
                <LabelledInput
                  label="Contact 1 name"
                  required
                  value={profile.emergencyContact1Name ?? ""}
                  onChange={(e) => set("emergencyContact1Name")(e.target.value)}
                />
                <LabelledInput
                  label="Contact 1 number"
                  required
                  type="tel"
                  value={profile.emergencyContact1Number ?? ""}
                  onChange={(e) => set("emergencyContact1Number")(e.target.value)}
                />
                <LabelledInput
                  label="Contact 2 name"
                  required
                  value={profile.emergencyContact2Name ?? ""}
                  onChange={(e) => set("emergencyContact2Name")(e.target.value)}
                />
                <LabelledInput
                  label="Contact 2 number"
                  required
                  type="tel"
                  value={profile.emergencyContact2Number ?? ""}
                  onChange={(e) => set("emergencyContact2Number")(e.target.value)}
                />
              </fieldset>
            </div>
          )}

          {step === 2 && (
            <div className="flex flex-col gap-3">
              {REQUIRED_DOCS.map((docType) => (
                <DocumentUploadRow key={docType} docType={docType} uploaded={uploaded.has(docType)} />
              ))}

              {missing && !missing.complete && (
                <div className="mt-2 rounded-xl border border-line bg-surface-sunken p-4">
                  <p className="eyebrow mb-2">Still outstanding</p>
                  <ul className="flex flex-col gap-1">
                    {missing.missingDates.map((field) => (
                      <li key={field} className="text-[13px] text-ink-soft">
                        {field === "dateOfBirth" ? "Date of birth" : "Date joined"}
                      </li>
                    ))}
                    {missing.missingProfileFields.map((field) => (
                      <li key={field} className="text-[13px] text-ink-soft">
                        {profileFieldLabel(field)}
                      </li>
                    ))}
                    {missing.missingDocTypes.map((docType) => (
                      <li key={docType} className="text-[13px] text-ink-soft">
                        {DOC_TYPE_LABEL[docType]} upload
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-5">
            <Button
              variant="ghost"
              size="sm"
              onClick={step === 0 ? logout : () => setStep((c) => c - 1)}
              disabled={busy}
            >
              {step === 0 ? "Sign out" : "Back"}
            </Button>

            {step < STEPS.length - 1 ? (
              <Button variant="primary" onClick={onNext} loading={busy} iconRight="arrow-right">
                Save and continue
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={onFinish}
                loading={complete.isPending}
                disabled={!missing?.complete}
                title={missing?.complete ? undefined : "Finish every outstanding item first"}
              >
                Finish setup
              </Button>
            )}
          </div>
        </>
      )}
    </AuthLayout>
  );
}

function DocumentUploadRow({ docType, uploaded }: { docType: DocType; uploaded: boolean }) {
  const upload = useUploadMyDocument();
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line p-3">
      <div className="flex min-w-0 items-center gap-2.5">
        <Icon
          name={uploaded ? "check-circle" : "document"}
          size={20}
          className={uploaded ? "text-good" : "text-faint"}
        />
        <div className="min-w-0">
          <p className="text-sm font-medium text-ink">{DOC_TYPE_LABEL[docType]}</p>
          <p className="text-xs text-muted">
            {uploaded ? "Uploaded" : "PDF or image, up to 5MB"}
            {error && <span className="text-bad"> · {error}</span>}
          </p>
        </div>
      </div>

      <label className="shrink-0">
        <span
          className={cn(
            "inline-flex h-9 cursor-pointer items-center gap-2 rounded-lg border border-line-strong px-4 text-sm font-medium",
            "transition-colors hover:bg-surface-sunken active:scale-[0.98]",
            upload.isPending && "pointer-events-none opacity-50",
          )}
        >
          <Icon name={upload.isPending ? "spinner" : "upload"} size={16} className={upload.isPending ? "animate-spin" : ""} />
          {uploaded ? "Replace" : "Upload"}
        </span>
        <input
          type="file"
          className="sr-only"
          accept="image/*,application/pdf"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (!file) return;
            setError(null);
            upload.mutate(
              { docType, file },
              {
                onError: (err) =>
                  setError(err instanceof ApiError ? err.message : "Upload failed."),
                onSuccess: () => toast.success(`${DOC_TYPE_LABEL[docType]} uploaded.`),
              },
            );
          }}
        />
      </label>
    </div>
  );
}
